import { RpcEngine } from './rpc.js'
import { METHODS, PUSH } from './events.js'
import { ChatModule } from '../modules/chat.js'
import { CharacterModule } from '../modules/character.js'
import { VariableModule } from '../modules/variable.js'
import { StorageModule } from '../modules/storage.js'
import { UIModule } from '../modules/ui.js'
import { MediaModule } from '../modules/media.js'
import { GameModule } from '../modules/game.js'
import { UtilsModule } from '../modules/utils.js'
import { WorldbookModule } from '../modules/worldbook.js'

export { METHODS, PUSH }

export class CharxSdk {
  private static instance: CharxSdk | null = null
  private rpc: RpcEngine
  private _ready = false

  private _chat?: ChatModule
  private _character?: CharacterModule
  private _variable?: VariableModule
  private _storage?: StorageModule
  private _ui?: UIModule
  private _media?: MediaModule
  private _game?: GameModule
  private _utils?: UtilsModule
  private _worldbook?: WorldbookModule

  private constructor() {
    this.rpc = new RpcEngine()
  }

  static getInstance(): CharxSdk {
    if (!CharxSdk.instance) {
      CharxSdk.instance = new CharxSdk()
    }
    return CharxSdk.instance
  }

  get isReady(): boolean { return this._ready }

  /**
   * Initialize the SDK.
   * Sends sdk.init to the platform and waits for a __charx_ready__ handshake
   * response containing a session token.  All subsequent RPC calls carry this
   * token — the platform rejects calls from frames that skipped the handshake.
   *
   * Throws if the platform does not respond within 8 seconds (not embedded in
   * the platform, or running in a non-iframe context).
   */
  async init(): Promise<void> {
    if (this._ready) return
    // Notify platform that the SDK is initialising
    this.rpc.post('sdk.init', [])
    // Wait for the platform to send back the session token
    await Promise.race([
      this.rpc.waitReady(),
      new Promise<void>((_, reject) =>
        setTimeout(() => reject(new Error(
          '[CharxSdk] init() timed out: SDK must run inside the platform iframe.'
        )), 8000)
      ),
    ])
    this._ready = true
  }

  getRpc(): RpcEngine { return this.rpc }

  get chat(): ChatModule {
    return (this._chat ??= new ChatModule(this.rpc))
  }
  get character(): CharacterModule {
    return (this._character ??= new CharacterModule(this.rpc))
  }
  get variable(): VariableModule {
    return (this._variable ??= new VariableModule(this.rpc))
  }
  get storage(): StorageModule {
    return (this._storage ??= new StorageModule(this.rpc))
  }
  get ui(): UIModule {
    return (this._ui ??= new UIModule(this.rpc))
  }
  get media(): MediaModule {
    return (this._media ??= new MediaModule(this.rpc))
  }
  get game(): GameModule {
    return (this._game ??= new GameModule(this.rpc))
  }
  get utils(): UtilsModule {
    return (this._utils ??= new UtilsModule(this.rpc))
  }
  get worldbook(): WorldbookModule {
    return (this._worldbook ??= new WorldbookModule(this.rpc))
  }
}
